/**
 * Document Repository
 * COMPLETE implementation for document management
 */

import { BaseRepository } from '../BaseRepository';
import { db } from '../db';
import type { Document } from '../../../types/database';

export class DocumentRepository extends BaseRepository<Document> {
  constructor() {
    super(db.documents, 'documents');
  }

  async findByCategory(category: string): Promise<Document[]> {
    return await this.table.where('category').equals(category).toArray();
  }

  async findByType(documentType: string): Promise<Document[]> {
    return await this.table.where('documentType').equals(documentType).toArray();
  }

  async findByAccessLevel(accessLevel: Document['accessLevel']): Promise<Document[]> {
    return await this.table.where('accessLevel').equals(accessLevel).toArray();
  }

  async findExpired(): Promise<Document[]> {
    const now = new Date();
    return await this.table.filter(doc =>
      doc.expiryDate !== undefined && new Date(doc.expiryDate) < now
    ).toArray();
  }

  async findExpiring(days: number): Promise<Document[]> {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);

    return await this.table.filter(doc => {
      if (!doc.expiryDate) return false;
      const expiry = new Date(doc.expiryDate);
      return expiry >= now && expiry <= futureDate;
    }).toArray();
  }

  async getByOrganization(organizationId: string): Promise<Document[]> {
    return await this.table.where('organizationId').equals(organizationId).toArray();
  }

  async search(searchTerm: string): Promise<Document[]> {
    const term = searchTerm.toLowerCase();
    return await this.table.filter(doc =>
      doc.filename.toLowerCase().includes(term) ||
      doc.title?.toLowerCase().includes(term) ||
      doc.category.toLowerCase().includes(term) ||
      (doc.tags !== undefined && doc.tags.some(tag => tag.toLowerCase().includes(term)))
    ).toArray();
  }

  async findByRelatedEntity(entityType: string, entityId: string): Promise<Document[]> {
    return await this.table.filter(doc =>
      doc.relatedEntityType === entityType && doc.relatedEntityId === entityId
    ).toArray();
  }

  async findRecent(limit: number = 10): Promise<Document[]> {
    const docs = await this.table.toArray();
    return docs
      .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())
      .slice(0, limit);
  }
}

export const documentRepository = new DocumentRepository();


